import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { usePermissionsContext } from '../../../context/permissionsContext';
import { AdvisoryRequestsResponse } from '../../../pages/myAdvisoryRequests/types';
import { makeAuthenticatedGetRequest } from '../../../services/axios';
import { myPlanAdvisoryRequests } from '../../../services/axios/endpoints';
import { OnboardingStep } from '../../../services/onboarding';
import { queryKeys } from '../../../services/react-query/queryKeys';
import { ExpertChatPreview, MeetingMessagePreview } from '../popover/HomeOnboardingPopover';

export const useHomeOnboardingSteps = (): OnboardingStep[] => {
  const { isFreemiumUser, userData } = usePermissionsContext();
  const benefits = userData?.benefits;

  const advisoryParams = { mode: 'dashboard' };
  const advisoryRequests = useQuery({
    queryKey: queryKeys.plan.advisoryRequests(advisoryParams),
    queryFn: async () => {
      const res = await makeAuthenticatedGetRequest<{
        message: string;
        data: AdvisoryRequestsResponse;
      }>(myPlanAdvisoryRequests, { params: advisoryParams });
      return res.data.data;
    },
    enabled: !isFreemiumUser,
    staleTime: 30_000,
  });

  const isAdvisorySectionVisible = !isFreemiumUser && !advisoryRequests.isError;
  const hasFastpass = benefits?.fastpass?.limit != null;
  const hasDeepDive = benefits?.deep_dive?.limit != null && !benefits?.deep_dive?.has_all_access;
  const isPlanBenefitsSectionVisible = !!benefits && (hasFastpass || hasDeepDive);

  return useMemo<OnboardingStep[]>(
    () => [
      {
        element: '[data-tour="home-ask-question"]',
        side: 'bottom',
        title: 'Ask our experts. Included in your plan.',
        body: 'Ask about a specific project, methodology, or your portfolio. A Calyx expert answers you directly, at no extra cost.',
        customContent: <ExpertChatPreview />,
        show: !isFreemiumUser,
      },
      {
        element: '[data-tour="home-request-call"]',
        side: 'bottom',
        title: 'Talk it through with an expert, at no extra cost.',
        body: 'Want to go deeper? We match you with the right expert and confirm a time.',
        customContent: <MeetingMessagePreview />,
        show: !isFreemiumUser,
      },
      {
        element: '[data-tour="home-fastpass"]',
        side: 'bottom',
        title: 'Skip the queue with a Fastpass',
        body: 'Pick any unrated project and our team will prioritize rating it, so you get answers faster when it matters.',
        show: isPlanBenefitsSectionVisible && hasFastpass,
      },
      {
        element: '[data-tour="home-deep-dive"]',
        side: 'bottom',
        title: 'Unlock a deep-dive analysis',
        body: 'Access a detailed analysis on a project from our ratings catalog. See the evidence, risks, and methodology behind the rating.',
        show: isPlanBenefitsSectionVisible && hasDeepDive,
      },
      {
        element: '[data-tour="home-advisory"]',
        side: 'bottom',
        title: 'Track every advisory request in one place.',
        body: "See status and timelines at a glance, and download finished reports the moment they're ready.",
        show: isAdvisorySectionVisible,
      },
    ],
    [
      isFreemiumUser,
      isPlanBenefitsSectionVisible,
      hasFastpass,
      hasDeepDive,
      isAdvisorySectionVisible,
    ]
  );
};
